import { useSearchParams } from 'react-router-dom';
import { INSIGHTS_SEO, getFeaturedArticle, topicFor } from '@/data/insights';
import { SITE_ORIGIN, useSeo } from '@/lib/seo';
import { useLiveTick } from '@/lib/liveContent';
import { InsightsHero } from '@/components/site/insights/InsightsHero';
import { FeaturedInsight } from '@/components/site/insights/FeaturedInsight';
import { TopicExplorer } from '@/components/site/insights/TopicExplorer';
import { ResourcesSection } from '@/components/site/insights/ResourcesSection';
import { RecommendedReading } from '@/components/site/insights/RecommendedReading';
import { ChallengeNav } from '@/components/site/insights/ChallengeNav';
import { InsightsSubscribe } from '@/components/site/insights/InsightsSubscribe';
import { CtaBand } from '@/components/site/CtaBand';

export default function InsightsPage() {
  useLiveTick();
  const origin = SITE_ORIGIN;
  const [searchParams] = useSearchParams();
  const featured = getFeaturedArticle();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const topicParam = searchParams.get('topic') || null;
  const activeTopic = topicParam ? topicFor(topicParam) : null;

  // Self-referencing canonicals per paginated URL; filtered views canonicalise
  // to the main hub so filter combinations never create duplicate indexed pages.
  const canonical =
    Number.isFinite(page) && page > 1 ? `${origin}/insights?page=${page}` : `${origin}/insights`;

  // Preserve reader context in the enquiry journey where practical.
  const discussHref = () =>
    `/?${activeTopic?.serviceHref ? `service=${activeTopic.key}&` : ''}source=${
      activeTopic ? `Insights (${activeTopic.label})` : 'Insights'
    }#solution-finder`;

  useSeo({
    title: INSIGHTS_SEO.title,
    description: INSIGHTS_SEO.description,
    canonical,
    ogImage: featured?.image?.src,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: INSIGHTS_SEO.title,
          description: INSIGHTS_SEO.description,
          url: `${origin}/insights`,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
            { '@type': 'ListItem', position: 2, name: 'Insights', item: `${origin}/insights` },
          ],
        },
      ],
    },
  });

  return (
    <>
      <InsightsHero />
      <FeaturedInsight />
      <TopicExplorer />
      <ResourcesSection />
      <RecommendedReading />
      <ChallengeNav />
      <InsightsSubscribe />
      <CtaBand
        index="08"
        eyebrow="Next step"
        heading="Need help applying this to your business?"
        copy="Amden combines practical advice with hands-on implementation across operations, people, growth and business launch."
        primaryLabel="Discuss Your Business"
        primaryHref={discussHref}
        secondaryLabel="Explore Our Services"
        secondaryHref="/services"
      />
    </>
  );
}
